import { Volume1Icon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { type MouseEvent, memo, useCallback, useMemo, useRef, useState } from 'react';

import { IconButton, type IconButtonProps } from '@air/primitive-icon-button';
import { Popover } from '@air/primitive-popover';
import { Slider } from '@air/primitive-slider';
import { formatVideoVolumeSliderTooltip } from '@air/utils-video';

import { usePlayerVolumeChange } from '../hooks/usePlayerVolumeChange';
import { usePlayerStore } from './PlayerContextProvider';

export type PlayerVolumeButtonProps = Omit<IconButtonProps, 'children' | 'label'>;

export const PlayerVolumeButton = memo((props: PlayerVolumeButtonProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const muted = usePlayerStore((state) => state.muted);
  const volume = usePlayerStore((state) => state.volume);
  const videoElement = usePlayerStore((state) => state.videoElement);
  const { onPlayerVolumeChange } = usePlayerVolumeChange();

  const handleMuteToggle = useCallback(() => {
    if (videoElement) {
      videoElement.muted = !videoElement.muted;
    }
  }, [videoElement]);

  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // Clicking on the button toggles the mute state. However, since it's rendered as a Popover trigger,
      // the popover will close when clicked. To prevent this, we need to prevent the default behavior
      // and stop the propagation of the event.
      event.preventDefault();
      event.stopPropagation();

      handleMuteToggle();
    },
    [handleMuteToggle],
  );

  const handlePointerEnter = useCallback(() => {
    setOpen(true);
  }, []);

  const handlePointerLeave = useCallback(() => {
    setOpen(false);
  }, []);

  const handleValueChange = useCallback(
    (value: number[]) => {
      const newVolume = value[0];

      onPlayerVolumeChange(newVolume);
    },
    [onPlayerVolumeChange],
  );

  const value = useMemo(() => {
    return [muted ? 0 : volume];
  }, [muted, volume]);

  const VolumeIcon = useMemo(() => {
    if (muted || volume === 0) {
      return VolumeXIcon;
    }

    if (volume < 0.5) {
      return Volume1Icon;
    }

    return Volume2Icon;
  }, [muted, volume]);

  return (
    <div onPointerLeave={handlePointerLeave} ref={containerRef}>
      <Popover
        className="w-8 min-w-[32px] overflow-visible"
        container={containerRef.current}
        onOpenChange={setOpen}
        open={open}
        side="top"
        trigger={
          <IconButton
            appearance="ghost"
            color="grey"
            data-muted={muted}
            data-testid="PLAYER_VOLUME_BUTTON"
            data-volume={volume}
            label="Volume"
            onClick={onClick}
            onPointerEnter={handlePointerEnter}
            {...props}
          >
            <VolumeIcon className="size-4" />
          </IconButton>
        }
      >
        <Slider
          className="h-20"
          defaultValue={value}
          formatTooltip={formatVideoVolumeSliderTooltip}
          max={1}
          min={0}
          onValueChange={handleValueChange}
          orientation="vertical"
          step={0.01}
          value={value}
        />
        {/* We need this div to cover the offset of the popover content to prevent the popover from closing */}
        <div className="absolute inset-x-0 -bottom-2 h-2 w-full" />
      </Popover>
    </div>
  );
});

PlayerVolumeButton.displayName = 'PlayerVolumeButton';
